import React, { useState } from 'react'
import { Alert, Button, DatePicker, Space, Typography, message } from 'antd'
import { CalendarOutlined, CheckCircleFilled } from '@ant-design/icons'
import moment from 'moment'
import adminRequest from 'utils/adminRequest'
import { buildEndDateNotice, formatEndDate } from './assessmentEndDateNotice'

const { Text } = Typography

/**
 * Tells the admin how long the assessment they are adding candidates to has
 * left, and makes them settle the date BEFORE they can add anyone — the rest of
 * the drawer stays blurred and inert until they either extend the end date or
 * say to keep it. The whole point is that the deadline is a decision, not a
 * footnote discovered after the candidates are already in.
 *
 * Deliberately moves the assessment's OWN end date rather than giving the new
 * candidates a private deadline: a per-candidate window is invisible on every
 * assessment-level screen (dates column, reports, at-risk bands), so the two
 * would disagree about when the assessment closes. Extending moves everyone
 * already on it as well, which is why the copy says so before confirming.
 *
 * Renders nothing without an end date, and the caller then leaves the gate
 * open — so the create-assessment flow and scheduled rows are unaffected.
 */
const AssessmentEndDateBanner = ({
  endDate,
  startDate,
  assessmentInstituteMapId,
  assessmentCorporateMapId,
  onEndDateUpdated,
  resolved = false,
  onResolvedChange,
}) => {
  const [editing, setEditing] = useState(false)
  const [newDate, setNewDate] = useState(null)
  const [saving, setSaving] = useState(false)
  // The banner keeps its own copy so the sentence updates the moment the save
  // succeeds, instead of waiting for the parent list to refetch behind the drawer.
  const [currentEndDate, setCurrentEndDate] = useState(endDate)

  const notice = buildEndDateNotice(currentEndDate, new Date(), startDate)
  if (!notice) return null

  const canSubmit = Boolean(assessmentInstituteMapId || assessmentCorporateMapId)

  const handleSave = async () => {
    if (!newDate) {
      message.warning('Pick a new end date')
      return
    }
    setSaving(true)
    try {
      // Reuse the same update contract as EditAssessmentDrawer. The selected
      // wall-clock digits are stored as-is, so do not send a timezone-bearing
      // ISO string (which would shift the admin's chosen time).
      const payload = {
        mapId: assessmentCorporateMapId || assessmentInstituteMapId,
        mapType: assessmentCorporateMapId ? 'corporate' : 'institute',
        endTime: newDate.format('YYYY-MM-DDTHH:mm:ss'),
      }
      const { data } = await adminRequest.put('/assessment/details', payload)
      const updated = data?.data?.current?.endTime || payload.endTime
      setCurrentEndDate(updated)
      setEditing(false)
      setNewDate(null)
      message.success(`End date updated to ${formatEndDate(updated)}`)
      if (onEndDateUpdated) onEndDateUpdated(updated)
      // Extending IS a decision, so it opens the gate on its own — the admin
      // should not have to confirm the date twice.
      if (onResolvedChange) onResolvedChange(true)
    } catch (error) {
      message.error(
        error?.response?.data?.message || error?.message || 'Could not update the end date'
      )
    } finally {
      setSaving(false)
    }
  }

  const startEditing = () => {
    setEditing(true)
    // Seed with the current end date so the picker opens on the right month
    // rather than today.
    setNewDate(currentEndDate ? moment.utc(currentEndDate) : null)
  }

  // Settled: stay visible but calm, and keep a way back in — an admin who
  // confirms too quickly should not have to close the drawer to fix it.
  if (resolved && !editing) {
    return (
      <Alert
        type={notice.tone}
        style={{ borderRadius: 0, padding: '8px 16px' }}
        icon={<CheckCircleFilled />}
        showIcon
        message={
          <Space wrap size={8}>
            <Text>
              Closes <strong>{formatEndDate(currentEndDate)}</strong> — {notice.days < 0
                ? 'already closed'
                : notice.days === 0
                  ? 'today'
                  : startDate
                    ? `${notice.daysAvailable} days of validity`
                    : notice.days === 1
                      ? 'tomorrow'
                      : `in ${notice.days} days`}
              .
            </Text>
            {canSubmit && (
              <Button type="link" size="small" style={{ padding: 0 }} onClick={startEditing}>
                Change end date
              </Button>
            )}
          </Space>
        }
      />
    )
  }

  return (
    <Alert
      type={notice.tone}
      showIcon
      style={{ borderRadius: 0, padding: '10px 16px' }}
      message={<span style={{ fontWeight: 600 }}>{notice.headline}</span>}
      description={
        <div>
          <div style={{ marginBottom: 8 }}>{notice.detail}</div>
          {!editing && (
            <Space wrap>
              {canSubmit && (
                <Button size="small" type="primary" icon={<CalendarOutlined />} onClick={startEditing}>
                  Extend end date
                </Button>
              )}
              <Button size="small" onClick={() => onResolvedChange && onResolvedChange(true)}>
                Keep this date
              </Button>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Choose one to start adding candidates.
              </Text>
            </Space>
          )}
          {editing && (
            <Space wrap>
              <DatePicker
                value={newDate}
                onChange={setNewDate}
                showTime={{ format: 'hh:mm A', use12Hours: true }}
                format="DD/MM/YYYY hh:mm A"
                allowClear={false}
                placeholder="Select end date & time"
                autoFocus
                // An end date in the past would close the assessment again the
                // moment it is saved.
                disabledDate={date => date && date < moment().startOf('day')}
              />
              <Button type="primary" size="small" loading={saving} onClick={handleSave}>
                Update
              </Button>
              <Button
                size="small"
                disabled={saving}
                onClick={() => {
                  setEditing(false)
                  setNewDate(null)
                }}
              >
                Cancel
              </Button>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Applies to everyone already on this assessment.
              </Text>
            </Space>
          )}
        </div>
      }
    />
  )
}

export default AssessmentEndDateBanner
